import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { AdvancedSettingsVirtualStepComponent } from 'wizards/steps/advanced-settings-virtual-step/advanced-settings-virtual-step.component';
import { AppAwareStepComponent } from 'wizards/steps/app-aware-step/app-aware-step.component';
import { ConsistencyStepComponent } from 'wizards/steps/archive-consistency-step/consistency-step.component';
import { ClusterStepComponent } from 'wizards/steps/cluster-step/cluster-step.component';
import { DestinationPlusRetentionStepComponent } from 'wizards/steps/combined/destination-plus-retention-step/destination-plus-retention-step.component';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { NBFRetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.component';
import { RetentionPolicyStepComponent } from 'wizards/steps/retention-policy-step/retention-policy-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectVirtualDisksStepComponent } from 'wizards/steps/select-virtual-disks-step/select-virtual-disks-step.component';
import { SelectVirtualMachinesStepComponent } from 'wizards/steps/select-virtual-machines-step/select-virtual-machines-step.component';
import { SimpleScheduleStepComponent } from 'wizards/steps/simple-schedule-step/simple-schedule-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { HyperVModelForStepsHelper } from '../../helpers/hyper-v-model-helpers/hyper-v-model-for-steps-helper';
import { HyperVPlanFromStepsHelper } from '../../helpers/hyper-v-model-helpers/hyper-v-plan-from-steps-helper';
import { HyperVFormModel } from '../../models/HyperV/hyper-v-form-model';
import { WizardsServicesHubService } from '../../services/wizards-services-hub.service';
import { VMWizardBaseClass } from '../vm-wizard-base.class';

type HyperVWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'destinationPlusRetention'
  | 'whatToBackUp'
  | 'cluster'
  | 'applicationProcessing'
  | 'advancedSettings'
  | 'selectVirtualDisks'
  | 'compressionEncryption'
  | 'backupConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-hyper-v-wizard',
  templateUrl: './hyper-v-wizard.component.html',
  providers: [
    {
      provide: VMWizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => HyperVWizardComponent)
    }
  ],
  imports: [
    AdvancedSettingsVirtualStepComponent,
    AppAwareStepComponent,
    AsyncPipe,
    ClusterStepComponent,
    CommonModule,
    CompressionEncryptionStepComponent,
    ConsistencyStepComponent,
    FormsModule,
    NBFRetentionPolicyStepComponent,
    NBFScheduleStepComponent,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RetentionPolicyStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectVirtualDisksStepComponent,
    SelectVirtualMachinesStepComponent,
    SimpleScheduleStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    DestinationPlusRetentionStepComponent
  ]
})
export class HyperVWizardComponent extends VMWizardBaseClass {
  i18nPipe: I18NextPipe;
  public readonly clusterTitle = this.i18nPipe.transform('wizards:clusterTitle');

  public FormModel: HyperVFormModel;
  public helpLink: WizardHelpLink<HyperVWizardHelpDocumentationLinks>;

  constructor() {
    const i18nPipe = inject(I18NextPipe);
    const services = inject(WizardsServicesHubService);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new HyperVFormModel(this.isUnix, this.isNBF, true);

    this.initSubscribes();
  }

  protected generateFormModel(plan) {
    this.FormModel = HyperVModelForStepsHelper.HyperVModelFromPlan(plan, this.defaultSettings());

    if (this.isEdit) setTimeout(() => this.storageManager.changeVMStorageHandler(this.getSelectedStorage));
  }

  changeHyperVStorageHandler(connection: StorageConnection): void {
    this.storageManager.changeVMStorageHandler(connection);
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  protected clearAppAwareData(newPlan) {
    HyperVPlanFromStepsHelper.clearAppAwareData(newPlan);
  }

  protected getPlanForSaving(): any {
    return HyperVPlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings());
  }

  getRAWHelpLinks(): Record<HyperVWizardHelpDocumentationLinks, string> {
    return this.isNBF
      ? {
          welcome: 'backup/hyper-v/nbf#planname',
          whereToBackUp: 'backup/hyper-v/nbf#storage',
          destinationPlusRetention: 'backup/hyper-v/nbf#storage',
          whatToBackUp: 'backup/hyper-v/nbf#server',
          cluster: 'backup/hyper-v/nbf#cluster',
          applicationProcessing: 'backup/hyper-v/nbf#app',
          advancedSettings: 'backup/hyper-v/nbf#advanced',
          selectVirtualDisks: 'backup/hyper-v/nbf#vdisks',
          compressionEncryption: 'backup/hyper-v/nbf#compression',
          backupConsistencyCheck: 'backup/hyper-v/nbf#ccheck',
          schedule: 'backup/hyper-v/nbf#schedule',
          retention: 'backup/hyper-v/nbf#retention',
          prePost: 'backup/hyper-v/nbf#prepost',
          notification: 'backup/hyper-v/nbf#log'
        }
      : {
          welcome: 'backup/hyper-v/legacy#plannameh',
          whereToBackUp: 'backup/hyper-v/legacy#storageh',
          destinationPlusRetention: '',
          whatToBackUp: 'backup/hyper-v/legacy#vmh',
          cluster: '',
          applicationProcessing: '',
          advancedSettings: 'backup/hyper-v/legacy#advancedh',
          selectVirtualDisks: 'backup/hyper-v/legacy#vdisksh',
          compressionEncryption: 'backup/hyper-v/legacy#compressionh',
          backupConsistencyCheck: 'backup/hyper-v/legacy#ccheckh',
          schedule: 'backup/hyper-v/legacy#scheduleh',
          retention: 'backup/hyper-v/legacy#retention',
          prePost: 'backup/hyper-v/legacy#preposth',
          notification: 'backup/hyper-v/legacy#notificationh'
        };
  }
}
